/**
 * autoStatus.js — DERIVED, READ-ONLY status for the monthly-work "Auto" deliverables.
 *
 * IMPORTANT: nothing here ever writes. It reads the live store and computes a status
 * on the fly from the real per-post / per-cell states your team already maintains
 * (calendar designStatus, mockup clientStatus + published flag, and the manager's
 * "awaiting client" flags). Because it only reads, it cannot corrupt or delete data —
 * that is the whole safety guarantee of the auto system.
 *
 * The golden rule encoded here: a deliverable is only "Client approved" when the CLIENT
 * has approved every non-pending cell on the published mockup. Internal reviewer
 * approval on the calendar tops out at "Ready for client" and never counts as done.
 */

// tone maps to the app's <Badge/> colors.
const S = {
  none:      { label: 'Not started',         tone: 'gray' },
  progress:  { label: 'In progress',         tone: 'blue' },
  building:  { label: 'Being built',         tone: 'blue' },
  ready:     { label: 'Ready for client',    tone: 'purple' },
  sent:      { label: 'Sent — awaiting client', tone: 'orange' },
  changes:   { label: 'Changes requested',   tone: 'red' },
  approved:  { label: 'Client approved',     tone: 'green' },
  done:      { label: 'Done',                tone: 'green' },
};

function calFor(store, clientId, month) {
  const cals = (store.contentCalendars || []).filter(c => c.clientId === clientId && c.monthYear === month);
  // newest first if several
  return cals.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0] || null;
}
function mockupFor(store, clientId, month) {
  const ms = (store.mockups || []).filter(m => m.clientId === clientId && m.monthYear === month);
  return ms.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0] || null;
}
function calPosts(cal) {
  if (!cal || !cal.data) return [];
  const grids = Array.isArray(cal.data.grids) ? cal.data.grids : [];
  const fromGrids = grids.flatMap(g => Array.isArray(g.posts) ? g.posts : []);
  if (fromGrids.length) return fromGrids;
  return Array.isArray(cal.data.posts) ? cal.data.posts : [];
}
const isAwaitingPost = p => !!p.awaitingClient;
const isAwaitingCell = c => !!c.awaitingClient;

// ── Planning ────────────────────────────────────────────────────────
export function planningStatus(store, clientId, month) {
  const cal = calFor(store, clientId, month);
  if (!cal) return { ...S.none };
  const assigned = (cal.data && cal.data.assignment && Array.isArray(cal.data.assignment.assignedToIds) && cal.data.assignment.assignedToIds.length > 0);
  return assigned ? { ...S.done, label: 'Planned & assigned' } : { ...S.progress, label: 'Calendar started — assign it' };
}

// ── Design (internal — never "approved") ────────────────────────────
export function designStatus(store, clientId, month) {
  const cal = calFor(store, clientId, month);
  const posts = calPosts(cal);
  if (!posts.length) return { ...S.none, done: 0, total: 0, awaiting: 0 };
  const awaiting = posts.filter(isAwaitingPost);
  const active = posts.filter(p => !isAwaitingPost(p));
  const approved = active.filter(p => p.designStatus === 'approved').length;
  const started = active.some(p => p.designStatus && p.designStatus !== 'todo');
  const base = active.length > 0 && approved === active.length
    ? { ...S.ready }
    : started ? { ...S.progress } : { ...S.none };
  return { ...base, done: approved, total: active.length, awaiting: awaiting.length };
}

// ── Mockup / client approval (the only real "approved" gate) ────────
export function mockupStatus(store, clientId, month) {
  const m = mockupFor(store, clientId, month);
  if (!m) return { ...S.none, done: 0, total: 0, awaiting: 0 };
  const cells = (m.data && Array.isArray(m.data.cells)) ? m.data.cells : [];
  const awaiting = cells.filter(isAwaitingCell);
  const active = cells.filter(c => !isAwaitingCell(c));
  const published = m.status === 'published' || !!(m.data && m.data.publishedAt);
  if (!published) return { ...S.building, done: 0, total: active.length, awaiting: awaiting.length };
  const approved = active.filter(c => c.clientStatus === 'approved').length;
  const anyChange = active.some(c => c.clientStatus === 'edit' || c.clientStatus === 'changed');
  const base = active.length > 0 && approved === active.length
    ? { ...S.approved }
    : anyChange ? { ...S.changes } : { ...S.sent };
  return { ...base, done: approved, total: active.length, awaiting: awaiting.length };
}

// ── Combined awaiting-client-content bucket (persists until resolved) ─
export function awaitingItems(store, clientId, month) {
  const cal = calFor(store, clientId, month);
  const m = mockupFor(store, clientId, month);
  const out = [];
  calPosts(cal).forEach((p, i) => { if (isAwaitingPost(p)) out.push({ where: 'Calendar', label: p.postContent || p.caption || `Post ${i + 1}`, note: p.awaitingNote || '' }); });
  const cells = (m && m.data && Array.isArray(m.data.cells)) ? m.data.cells : [];
  cells.forEach((c, i) => { if (isAwaitingCell(c)) out.push({ where: 'Mockup', label: c.caption || `Post ${i + 1}`, note: c.awaitingNote || '' }); });
  return out;
}

// Convenience: the auto status for a given monthly-work cycleKey.
export function autoStatusFor(cycleKey, store, clientId, month) {
  if (cycleKey === 'planning') return planningStatus(store, clientId, month);
  if (cycleKey === 'design')   return designStatus(store, clientId, month);
  if (cycleKey === 'mockup')   return mockupStatus(store, clientId, month);
  return null;
}
